import * as React from 'react';
import { t } from '@lingui/core/macro';
import VideoStream from 'components/VideoStream';
import { Video } from 'components/Video';
import { myCameraStream } from 'state/video';
import ParticipantVideo from 'components/ParticipantVideo';
import Icon from 'components/Icon';
import useVoipConnections from './useVoipConnections';
import { useWebComponent } from 'components/WebComponentRoot';
import SessionControls from 'components/SessionControls';
import EndSessionButton from 'components/EndSessionButton';
import { MultiVoipProps } from './MultiVoip';
import VideoPlaceholder from 'components/VideoPlaceholder';
import VoipStandbyTakeover from 'components/VoipStandbyTakeover';
import Crossfade from 'components/Crossfade';
import { performVoipTakeover, userSession } from 'state/session';
import { VOIP_CONFIG } from 'config/voip';
import useIsPhone from 'hooks/useIsPhone';

type MultiVoipInternalProps = MultiVoipProps & {
  onEndSession?: () => void;
  /**
   * Lifts the "any remote is the active speaker" boolean up to the parent (View.tsx → VoipPanel)
   * so the collapsed pill can pulse without re-mounting `useVoipConnections` (which drives
   * `voipRtcConnection.publish()` and must mount exactly once).
   */
  onActiveSpeakerChange?: (isActiveSpeaker: boolean) => void;
};

export const MultiVoipInternal: React.FC<MultiVoipInternalProps> = ({
  onEndSession,
  controls,
  className,
  onActiveSpeakerChange,
  onActiveParticipantChange,
  onSelfMirrorChange,
  extraControls,
  maxParticipants,
}) => {
  const wcContext = useWebComponent();
  const showControls = wcContext?.controls !== undefined || controls;
  const { canShowVoip, remoteConnections, activeConnectionId, sortedRemotes, isAnyActiveSpeaker, selfMirrorRemote } =
    useVoipConnections();
  const elementRef = React.useRef<HTMLDivElement>(null);
  const isPhone = useIsPhone();

  // AVS-4302 (§5.5, CG-6): a standby dealer's "You" tile mirrors its own face via a
  // REMOTE loopback (the master's published stream), not `myCameraStream` (which a
  // standby never grabs). Mute/video state must come from THAT remote's tracks. When
  // there's no self-mirror (master / customer / flag-off / loopback not yet arrived),
  // the tile renders `myCameraStream` exactly as today.
  const isStandby = userSession().isVoipStandby;
  // AVS-4302: with loopback disabled (default), a standby suppresses ALL incoming media —
  // the self tile shows a dark placeholder + "Takeover" CTA and useVoipConnections empties
  // the remote render list (PO: only the Takeover tile), while the RTC layer independently
  // disables the received tracks (RTCClient.receiveOnly).
  const showStandbyPlaceholder = userSession().isVoipIncomingMediaSuppressed;
  const selfMirrorStream = selfMirrorRemote?.stream instanceof MediaStream ? selfMirrorRemote.stream : null;
  const selfAudioMuted = selfMirrorRemote ? !!selfMirrorRemote.audioMuted : !myCameraStream().get('isAudioEnabled');
  const selfVideoMuted = selfMirrorRemote ? !!selfMirrorRemote.videoMuted : !myCameraStream().get('isVideoEnabled');

  // AVS-4302: identity of the self-tile's current video SOURCE. A change drives the crossfade —
  // standby loopback ⇄ own camera on takeover/step-down — so the swap has no hard cut or black gap.
  // Keyed on the loopback stream id so a NEW master's loopback (e.g. a third tab takes over) also
  // crossfades rather than jump-cutting.
  const ownCameraReady = !!myCameraStream().get('mediaAllowed') && !!myCameraStream().stream;
  const selfSourceKey = showStandbyPlaceholder
    ? 'standby-takeover'
    : selfMirrorStream
      ? `loopback:${selfMirrorStream.id}`
      : ownCameraReady
        ? 'self-camera'
        : 'placeholder';

  // Caller's explicit value always wins; otherwise phone gets auto-2, desktop stays uncapped.
  // One tile is always self, so the remote allocation is `effectiveMax - 1`.
  const effectiveMax = maxParticipants ?? (isPhone ? 2 : undefined);
  const remoteSlots = effectiveMax === undefined ? Number.POSITIVE_INFINITY : Math.max(0, effectiveMax - 1);

  // sortedRemotes already places isActiveSpeaker/lastActiveSpeaker first, so slicing from
  // the start keeps the active speaker visible whenever at least one remote slot exists.
  const visibleRemotes = React.useMemo(() => {
    if (sortedRemotes.length === 0) return [];
    if (remoteSlots === Number.POSITIVE_INFINITY) return sortedRemotes;
    return sortedRemotes.slice(0, remoteSlots);
  }, [sortedRemotes, remoteSlots]);

  // Subscribe to dealer name from session so the chip and ARIA label stay in sync.
  const session = userSession().useState();
  const dealerName = session.dealerName;

  React.useEffect(() => {
    onActiveSpeakerChange?.(isAnyActiveSpeaker);
  }, [isAnyActiveSpeaker, onActiveSpeakerChange]);

  const activeConnection = remoteConnections?.[activeConnectionId];
  const activeStream = activeConnection?.stream instanceof MediaStream ? activeConnection.stream : null;
  const activeAudioMuted = !!activeConnection?.audioMuted;
  const activeVideoMuted = !!activeConnection?.videoMuted;

  // Tracks true unmount so the participant-emit cleanup only fires on unmount, not on
  // every dep-change re-fire (which would cause transient `null` flashes for consumers).
  // Initialised `false` and set `true` inside the effect — the idiomatic pattern; effects
  // always run before any cleanup can fire, so `false` is never observed during a real
  // dep-change cycle.
  const isMountedRef = React.useRef(false);
  React.useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  React.useEffect(() => {
    if (!onActiveParticipantChange) return;
    if (activeStream) {
      onActiveParticipantChange({
        stream: activeStream,
        audioMuted: activeAudioMuted,
        videoMuted: activeVideoMuted,
      });
    } else {
      onActiveParticipantChange(null);
    }
    // Initial mount race note: useVoipConnections seeds activeConnectionId to ''
    // and resolves it inside its own useEffect. With existing remote connections,
    // this means we emit null on the first render and the real participant on the
    // second. Acceptable; the consumer (VoipPanel) tolerates the flip.
    return () => {
      // Defensive cleanup: emit null only on true unmount so consumers holding a tile
      // reference don't keep a detached MediaStream. Gating with `isMountedRef` avoids
      // a transient `null` between dep-change re-fires (cleanup fires on every dep
      // change, but only the last one — the unmount — should clear consumer state).
      if (!isMountedRef.current) {
        onActiveParticipantChange(null);
      }
    };
  }, [activeStream, activeAudioMuted, activeVideoMuted, onActiveParticipantChange]);

  // AVS-4302: lift the standby's self-mirror loopback to VoipPanel so the COLLAPSED pill can
  // show the dealer's own face (the master tab's stream) in its "You" tile — a standby has no
  // `myCameraStream`, so without this the minimized pill only ever shows the avatar placeholder.
  // `null` for a master / customer / flag-off tab or before the loopback arrives (the pill then
  // falls back to `myCameraStream` exactly as today). Same mount-safety as the participant emit.
  React.useEffect(() => {
    if (!onSelfMirrorChange) return;
    onSelfMirrorChange(
      VOIP_CONFIG.loopbackVideo && selfMirrorStream
        ? { stream: selfMirrorStream, audioMuted: selfAudioMuted, videoMuted: selfVideoMuted }
        : null
    );
    return () => {
      if (!isMountedRef.current) {
        onSelfMirrorChange(null);
      }
    };
  }, [selfMirrorStream, selfAudioMuted, selfVideoMuted, onSelfMirrorChange]);

  return (
    canShowVoip && (
      <div className={className} ref={elementRef}>
        {showControls && (
          <div className="ex3d-stream--voip-controls" part="voip-controls">
            <SessionControls voipActive>
              <EndSessionButton onEndSession={onEndSession} />
              {extraControls}
            </SessionControls>
          </div>
        )}
        <div className="ex3d-stream-videos" part="videos">
          <ParticipantVideo
            // Standby placeholder owns its own visuals (dark backdrop + CTA); force both muted
            // flags off so ParticipantVideo renders our children verbatim instead of collapsing
            // them / swapping in its own placeholder when the master's camera is muted.
            audioMuted={showStandbyPlaceholder ? false : selfAudioMuted}
            videoMuted={showStandbyPlaceholder ? false : selfVideoMuted}
            className="ex3d-video--self"
            aria-label={t`Your camera`}
          >
            <span className="ex3d-voip-tile-label">{t`You`}</span>
            <Crossfade transitionKey={selfSourceKey}>
              {showStandbyPlaceholder ? (
                // AVS-4302: loopback disabled — render a dark backdrop instead of the master's
                // mirror. The interactive "Takeover" CTA is a sibling overlay (below), kept out
                // of the crossfade layer so it never lands in an aria-hidden outgoing fade.
                <div className="ex3d-voip-standby-backdrop" aria-hidden="true" />
              ) : selfMirrorStream ? (
                // Standby self-mirror: render the loopback remote. Force `audioMuted` — this
                // tile is a mirror of the dealer's own face, so its audio must never play
                // back (the live call audio rides the master tab), otherwise it echoes.
                <Video stream={selfMirrorStream} audioMuted videoClassName="size-full object-cover" />
              ) : ownCameraReady ? (
                <VideoStream autoStart={true} containerClassName="max-w-l" controlsClassName="hidden" />
              ) : (
                <VideoPlaceholder />
              )}
            </Crossfade>
            {isStandby && VOIP_CONFIG.loopbackVideo && (
              // AVS-4302 (§5.4): manual takeover affordance. The dim (PO-required receive-only
              // cue) is KEPT but lightened and rounded to the tile so it no longer reads as a
              // hard box-in-a-box. The explanation moves to a top-right "presenting elsewhere"
              // cast pip (sighted: hover tooltip; assistive tech: the visually-hidden caption),
              // and the action is a compact transfer badge pinned bottom-right — so the dealer's
              // own face stays clear of any control. Only present on a standby dealer (isStandby
              // is false for master/customer/flag-off).
              <div className="ex3d-voip-takeover-overlay">
                <span className="ex3d-voip-takeover-caption">{t`You're presenting in another tab`}</span>
                <span className="ex3d-voip-takeover-pip" title={t`You're presenting in another tab`} aria-hidden="true">
                  <Icon className="ex3d-icon--cast" />
                </span>
                <button
                  type="button"
                  className="ex3d-button--primary ex3d-voip-takeover-button"
                  aria-label={t`Take over`}
                  onClick={() => {
                    void performVoipTakeover();
                  }}
                >
                  <Icon className="ex3d-icon--bidirectional" />
                  <span className="ex3d-voip-takeover-tooltip" aria-hidden="true">{t`Take over`}</span>
                </button>
              </div>
            )}
            {showStandbyPlaceholder && (
              // AVS-4302: loopback disabled — the takeover affordance is a centered CTA over the
              // dark backdrop (sibling of Crossfade for a11y, not a child of it).
              <VoipStandbyTakeover onTakeover={() => performVoipTakeover()} />
            )}
          </ParticipantVideo>
          {visibleRemotes.map((remote, index) => {
            // dealerName is a single session-scoped scalar — only the active-speaker slot
            // (index 0, guaranteed by sortedRemotes ordering) wears it. Non-active tiles
            // get a neutral aria-label and no name chip to avoid misattributing the dealer
            // identity to every participant in multi-remote calls.
            const isPrimary = index === 0;
            const ariaLabel = isPrimary
              ? dealerName
                ? t`${dealerName} camera`
                : t`Dealer camera`
              : t`Participant camera`;
            return (
              <ParticipantVideo
                key={remote.stream?.id || remote.connectionId}
                audioMuted={!!remote.audioMuted}
                videoMuted={!!remote.videoMuted}
                className={remote.isActiveSpeaker ? 'ex3d-video--active-speaker' : 'ex3d-video--inactive-speaker'}
                aria-label={ariaLabel}
                data-active-speaker={remote.isActiveSpeaker ? 'true' : 'false'}
              >
                {isPrimary && dealerName ? <span className="ex3d-voip-tile-label">{dealerName}</span> : null}
                {remote.stream && remote.stream instanceof MediaStream ? (
                  <Video
                    stream={remote.stream}
                    audioMuted={remote.audioMuted}
                    videoClassName="size-full object-cover"
                  />
                ) : (
                  <VideoPlaceholder />
                )}
              </ParticipantVideo>
            );
          })}
          {/* AVS-4275: no "awaiting participant" placeholder tile — when alone in the
              call we render only the self tile so the panel collapses to one face,
              mirroring the minimized pill which also drops the second tile. */}
        </div>
      </div>
    )
  );
};

export default MultiVoipInternal;
